import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Objective, ObjectiveStatus, Milestone } from '../../types/pms';
import ProgressSlider from './ProgressSlider';
import { OBJECTIVES_LOCKED_MESSAGE } from '../../services/pmsWorkflow';
import './GoalsTable.css';

interface GoalsTableProps {
  objectives: Objective[];
  milestonesByObjective: Record<string, Milestone[]>;
  onObjectiveClick?: (objective: Objective) => void;
  onDeleteObjective?: (objectiveId: string) => void;
  onEditObjective?: (objective: Objective) => void;
  onProgressChange?: (objectiveId: string, value: number) => void;
  onMilestoneProgressChange?: (objectiveId: string, milestoneId: string, value: number) => void;
  isReadOnly?: boolean;
  /**
   * Whether HR has locked the whole cycle. Separate from each objective's own `isLocked`
   * because an HR lock is written to the cycle row only — see isObjectiveLockedByHR.
   */
  cycleLocked?: boolean;
  /** Hide the completion summary cards (when parent already shows metrics) */
  hideCompletionSummary?: boolean;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getStatusClass(status: string): string {
  const s = status.toLowerCase().replace(/[_ ]/g, '-');
  return `gt-status--${s}`;
}

const GoalsTable: React.FC<GoalsTableProps> = ({
  objectives,
  milestonesByObjective,
  onObjectiveClick,
  onDeleteObjective,
  onEditObjective,
  onProgressChange,
  onMilestoneProgressChange,
  isReadOnly = false,
  cycleLocked = false,
  hideCompletionSummary = false,
}) => {
  const [expandedGoals, setExpandedGoals] = useState<Set<string>>(new Set());
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [draftProgressById, setDraftProgressById] = useState<Record<string, number>>({});
  const [draftMilestoneProgressById, setDraftMilestoneProgressById] = useState<Record<string, number>>({});

  useEffect(() => {
    setDraftProgressById((current) => {
      const next: Record<string, number> = {};
      Object.entries(current).forEach(([objectiveId, progress]) => {
        const objective = objectives.find((item) => item.id === objectiveId);
        if (objective && clampProgress(objective.progressPercentage) !== progress) {
          next[objectiveId] = progress;
        }
      });
      const nextKeys = Object.keys(next);
      const currentKeys = Object.keys(current);
      const unchanged = nextKeys.length === currentKeys.length && nextKeys.every((key) => current[key] === next[key]);
      return unchanged ? current : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectives]);

  useEffect(() => {
    setDraftMilestoneProgressById((current) => {
      const next: Record<string, number> = {};
      Object.entries(current).forEach(([milestoneId, progress]) => {
        const milestone = Object.values(milestonesByObjective).flat().find((item) => item.id === milestoneId);
        if (milestone && clampProgress(milestone.progressPercentage) !== progress) {
          next[milestoneId] = progress;
        }
      });
      const nextKeys = Object.keys(next);
      const currentKeys = Object.keys(current);
      const unchanged = nextKeys.length === currentKeys.length && nextKeys.every((key) => current[key] === next[key]);
      return unchanged ? current : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [milestonesByObjective]);

  const toggleGoal = (id: string) => {
    setExpandedGoals((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalWeightage = useMemo(
    () => objectives.reduce((sum, o) => sum + o.weightage, 0),
    [objectives]
  );

  const completionSummary = useMemo(() => ({
    completed: objectives.filter((o) => o.status === ObjectiveStatus.COMPLETED).length,
    inProgress: objectives.filter((o) =>
      [ObjectiveStatus.IN_PROGRESS, ObjectiveStatus.APPROVED, ObjectiveStatus.UNDER_REVIEW].includes(o.status)
    ).length,
    pendingApproval: objectives.filter((o) =>
      [ObjectiveStatus.SUBMITTED, ObjectiveStatus.DRAFT, ObjectiveStatus.RETURNED].includes(o.status)
    ).length,
  }), [objectives]);

  /*
   * Progress stays editable until the objective is CLOSED — closed meaning the cycle is
   * historical, HR has locked the cycle, or the objective itself is locked (all three arrive as
   * isReadOnly / isLocked). Status deliberately does not gate this.
   *
   * It used to: Under_Review and Completed were excluded, so an employee whose goal had moved to
   * either state found the slider frozen at whatever number it held when the status flipped —
   * with no way to record work actually finished, and nothing on the row explaining why.
   * Reaching 100% sets the status to Completed, which is exactly when people want to correct an
   * over- or under-shoot, so that was the worst possible place to stop accepting input.
   *
   * This only relaxes the UI to match what the save path already allowed: handleProgressChange
   * in PMSRouteWrappers gates on objective.isLocked and the parent cycle's lock, and has never
   * checked status.
   */
  const canEditProgress = (objective: Objective): boolean =>
    !isReadOnly && !objective.isLocked && Boolean(onProgressChange);

  const clampProgress = (value: number): number =>
    Math.round(Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0)));

  const getDisplayProgress = (objective: Objective): number =>
    draftProgressById[objective.id] ?? clampProgress(objective.progressPercentage);

  const getDisplayMilestoneProgress = (milestone: Milestone): number =>
    draftMilestoneProgressById[milestone.id] ?? clampProgress(milestone.progressPercentage);

  const setDraftProgress = (objectiveId: string, value: number) => {
    const progress = clampProgress(value);
    setDraftProgressById((current) => ({
      ...current,
      [objectiveId]: progress,
    }));
    onProgressChange?.(objectiveId, progress);
  };

  /** Same rule as canEditProgress above — the milestone follows its objective. */
  const canEditMilestoneProgress = (objective: Objective): boolean =>
    !isReadOnly && !objective.isLocked && Boolean(onMilestoneProgressChange);

  const setDraftMilestoneProgress = (objectiveId: string, milestoneId: string, value: number) => {
    const progress = clampProgress(value);
    setDraftMilestoneProgressById((current) => ({
      ...current,
      [milestoneId]: progress,
    }));
    onMilestoneProgressChange?.(objectiveId, milestoneId, progress);
  };

  const getMilestoneStatusLabel = (progress: number): string => {
    if (progress >= 100) return 'Completed';
    if (progress > 0) return 'In Progress';
    return 'Not Started';
  };

  return (
    <div className="gt-wrapper">
      {!hideCompletionSummary && (
      <div className="gt-completion-summary">
        <motion.div
          className="gt-completion-card"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <span className="gt-completion-card__label">Completed</span>
          <strong className="gt-completion-card__value gt-completion-card__value--done">{completionSummary.completed}</strong>
        </motion.div>
        <motion.div
          className="gt-completion-card"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.05 }}
        >
          <span className="gt-completion-card__label">In Progress</span>
          <strong className="gt-completion-card__value gt-completion-card__value--active">{completionSummary.inProgress}</strong>
        </motion.div>
        <motion.div
          className="gt-completion-card"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1 }}
        >
          <span className="gt-completion-card__label">Pending Approval</span>
          <strong className="gt-completion-card__value gt-completion-card__value--pending">{completionSummary.pendingApproval}</strong>
        </motion.div>
      </div>
      )}

      <div className="gt-table-scroll">
      <table className="gt-table">
        <thead>
          <tr>
            <th className="gt-th gt-th--expand" style={{ width: 40 }}></th>
            <th className="gt-th gt-th--category">Category</th>
            <th className="gt-th gt-th--name">Goals and Milestones</th>
            <th className="gt-th gt-th--weight">Weightage</th>
            <th className="gt-th gt-th--progress">Progress</th>
            <th className="gt-th gt-th--due">Due On</th>
            <th className="gt-th gt-th--status">Status</th>
            {!isReadOnly && (onDeleteObjective || onEditObjective) && (
              <th className="gt-th gt-th--actions">Actions</th>
            )}
          </tr>
        </thead>
        <tbody>
          {objectives.length === 0 && (
            <tr>
              <td colSpan={!isReadOnly && (onDeleteObjective || onEditObjective) ? 8 : 7} className="gt-empty">
                No objectives found for this review cycle.
              </td>
            </tr>
          )}
          {objectives.map((obj) => {
            const milestones = milestonesByObjective[obj.id] || [];
            const isExpanded = expandedGoals.has(obj.id);
            return (
              <React.Fragment key={obj.id}>
                {/* Goal parent row */}
                <tr
                  className={`gt-row gt-row--goal ${isExpanded ? 'gt-row--expanded' : ''}`}
                  onClick={() => onObjectiveClick?.(obj)}
                >
                  <td className="gt-td gt-td--expand">
                    {milestones.length > 0 && (
                      <button
                        className={`gt-chevron ${isExpanded ? 'gt-chevron--open' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleGoal(obj.id);
                        }}
                        aria-label={isExpanded ? 'Collapse milestones' : 'Expand milestones'}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    )}
                  </td>
                  <td className="gt-td gt-td--category">
                    <span className="gt-category-badge">Goal</span>
                  </td>
                  <td className="gt-td gt-td--name">
                    <div className="gt-goal-name">
                      {(obj.isLocked || cycleLocked) && (
                        // Titled, not a bare glyph: the progress slider below goes read-only
                        // when this is set, and an unexplained icon leaves the employee
                        // thinking the control is broken rather than deliberately locked.
                        // cycleLocked is OR-ed in because an HR cycle lock never sets the
                        // objective's own flag, so this row showed no reason for the freeze.
                        <span
                          className="gt-lock"
                          aria-label="Locked by HR"
                          title={OBJECTIVES_LOCKED_MESSAGE}
                        >
                          🔒
                        </span>
                      )}
                      <span className="gt-goal-title">{obj.title}</span>
                    </div>
                  </td>
                  <td className="gt-td gt-td--weight">
                    <span className="gt-weight-value">{obj.weightage}%</span>
                  </td>
                  <td
                    className="gt-td gt-td--progress"
                    onClick={(event) => event.stopPropagation()}
                    onPointerDown={(event) => event.stopPropagation()}
                  >
                    <ProgressSlider
                      className="gt-progress-control"
                      value={getDisplayProgress(obj)}
                      onChange={canEditProgress(obj) ? (progress) => setDraftProgress(obj.id, progress) : undefined}
                      readOnly={!canEditProgress(obj)}
                      ariaLabel={`Update progress for ${obj.title}`}
                    />
                  </td>
                  <td className="gt-td gt-td--due">{formatDate(obj.endDate)}</td>
                  <td className="gt-td gt-td--status">
                    <span className={`gt-status ${getStatusClass(obj.status)}`}>
                      {obj.status.replace('_', ' ')}
                    </span>
                  </td>
                  {!isReadOnly && (onDeleteObjective || onEditObjective) && (
                    <td className="gt-td gt-td--actions" onClick={e => e.stopPropagation()}>
                      {(obj.status === ObjectiveStatus.DRAFT || obj.status === ObjectiveStatus.SUBMITTED || obj.status === ObjectiveStatus.RETURNED || obj.status === ObjectiveStatus.APPROVED || obj.status === ObjectiveStatus.IN_PROGRESS) && (
                        <div className="gt-action-btns">
                          {onEditObjective && (
                            <button className="gt-action-btn gt-action-btn--edit" onClick={() => onEditObjective(obj)} title="Edit goal">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            </button>
                          )}
                          {onDeleteObjective && (obj.status === ObjectiveStatus.DRAFT || obj.status === ObjectiveStatus.RETURNED) && (
                            confirmDeleteId === obj.id ? (
                              <div className="gt-confirm-delete">
                                <span className="gt-confirm-text">Delete?</span>
                                <button className="gt-action-btn gt-action-btn--confirm" onClick={() => { onDeleteObjective(obj.id); setConfirmDeleteId(null); }} title="Confirm delete">✓</button>
                                <button className="gt-action-btn gt-action-btn--cancel" onClick={() => setConfirmDeleteId(null)} title="Cancel">✕</button>
                              </div>
                            ) : (
                              <button className="gt-action-btn gt-action-btn--delete" onClick={() => setConfirmDeleteId(obj.id)} title="Delete goal">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                              </button>
                            )
                          )}
                        </div>
                      )}
                    </td>
                  )}
                </tr>

                {/* Milestone child rows */}
                {isExpanded &&
                  milestones.map((ms) => {
                    const isOverdue =
                      ms.dueDate < new Date().toISOString() && ms.progressPercentage < 100;
                    const milestoneProgress = getDisplayMilestoneProgress(ms);
                    const milestoneStatus = getMilestoneStatusLabel(milestoneProgress);
                    return (
                      <tr key={ms.id} className="gt-row gt-row--milestone">
                        <td className="gt-td gt-td--expand"></td>
                        <td className="gt-td gt-td--category">
                          <span className="gt-category-badge gt-category-badge--ms">Milestone</span>
                        </td>
                        <td className="gt-td gt-td--name">
                          <div className="gt-milestone-name">
                            <span className="gt-ms-connector"></span>
                            <span>{ms.title}</span>
                          </div>
                        </td>
                        <td className="gt-td gt-td--weight">
                          <span className="gt-weight-value gt-weight-value--ms">{ms.weightage}%</span>
                        </td>
                        <td
                          className="gt-td gt-td--progress"
                          onClick={(event) => event.stopPropagation()}
                          onPointerDown={(event) => event.stopPropagation()}
                        >
                          <ProgressSlider
                            className="gt-progress-control gt-progress-control--milestone"
                            value={milestoneProgress}
                            onChange={canEditMilestoneProgress(obj) ? (progress) => setDraftMilestoneProgress(obj.id, ms.id, progress) : undefined}
                            readOnly={!canEditMilestoneProgress(obj)}
                            size="sm"
                            ariaLabel={`Update progress for milestone ${ms.title}`}
                          />
                        </td>
                        <td className="gt-td gt-td--due">
                          <span className={isOverdue ? 'gt-overdue' : ''}>
                            {formatDate(ms.dueDate)}
                            {isOverdue && ' ⚠'}
                          </span>
                        </td>
                        <td className="gt-td gt-td--status">
                          <span className={`gt-status ${milestoneProgress === 100 ? 'gt-status--completed' : milestoneProgress > 0 ? 'gt-status--in-progress' : 'gt-status--draft'}`}>
                            {milestoneStatus}
                          </span>
                        </td>
                        {!isReadOnly && (onDeleteObjective || onEditObjective) && (
                          <td className="gt-td gt-td--actions"></td>
                        )}
                      </tr>
                    );
                  })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      </div>

      {/* Footer */}
      <div className="gt-footer">
        <div className="gt-footer-weightage">
          <span>Total Goals Weightage:</span>
          <span className={`gt-footer-value ${totalWeightage === 100 ? 'gt-footer-value--ok' : 'gt-footer-value--warn'}`}>
            {totalWeightage}/100
          </span>
        </div>
      </div>
    </div>
  );
};

export default GoalsTable;
